
import { Fragment } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from '@headlessui/react'

interface ModalProps {
	readonly children: React.ReactNode;
	readonly title: string | React.ReactNode;
	readonly isOpen: boolean;
	readonly onClose: () => void;
	readonly isClosableOnOutsideClick?: boolean | undefined;
	readonly size?: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'full' | undefined;
}

export default function Modal({ children, title, isOpen, onClose, isClosableOnOutsideClick = true, size = 'large' }: ModalProps) {
	const sizes = {
		small: 'md:w-[400px]',
		medium: 'md:w-[540px]',
		large: 'md:w-[720px]',
		xlarge: 'md:w-[1080px]',
		xxlarge: 'md:w-3/4',
		full: 'md:w-[100vw]'
	}

	const sizeClass = sizes[size]
	const titlePadding = size === 'small' ? '' : size === 'medium' ? 'pb-4' : 'pb-6'

	return (
		<Transition
			appear
			show={ isOpen }
			as={ Fragment }
		>
			<Dialog
				as="div"
				className="relative z-10"
				onClose={ isClosableOnOutsideClick ? onClose : () => null }
			>
				<TransitionChild
					as={ Fragment }
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className="fixed inset-0 bg-black bg-opacity-50" />
				</TransitionChild>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<TransitionChild
							as={ Fragment }
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<DialogPanel className={ 'transform rounded-2xl bg-white text-left align-middle shadow-xl transition-all ' + sizeClass }>
								<div className={ 'flex justify-between items-center px-6 pt-6 ' + titlePadding }>
									<DialogTitle
										as="h2"
										className="text-xl font-bold leading-6 text-gray-900"
									>
										{ title }
									</DialogTitle>
									<button
										className="p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 outline-none transition-all"
										onClick={ onClose }
									>
										<div className="h-6 w-6 p-1 flex justify-center items-center">
											<FontAwesomeIcon icon="times" style={ { width: '24px' } } />
										</div>
									</button>
								</div>
								<div className="p-6 pt-3">
									{ children }
								</div>
							</DialogPanel>
						</TransitionChild>
					</div>
				</div>
			</Dialog>
		</Transition>
	)
}
